const FALLBACK_DEAL_LABEL = "Limited time deal";

/** Discounts below this percentage are too small to badge. */
export const MIN_DISCOUNT_PERCENTAGE = 10;

/**
 * A store discount is shown only when it is at least
 * {@link MIN_DISCOUNT_PERCENTAGE}% off.
 */
export function isDisplayableDiscount<T extends { percentage: number }>(
  discount: T | null | undefined,
): discount is T {
  return discount != null && discount.percentage >= MIN_DISCOUNT_PERCENTAGE;
}

/**
 * A store deal is active when it exists and its endTime (if any) is still in
 * the future. Deals without an endTime (e.g. Walmart) never expire client-side.
 * Doubles as a type guard so callers can use `deal` without a null check.
 */
export function isStoreDealActive<T extends { endTime?: Date }>(
  deal: T | null | undefined,
): deal is T {
  if (!deal) return false;
  if (!deal.endTime) return true;
  return deal.endTime.getTime() > Date.now();
}

/**
 * Display label for a store deal. Some upstream labels (e.g. Amazon's bare
 * "Ends in ") only make sense next to a countdown, so empty or bare "Ends in"
 * labels fall back to "Limited time deal".
 */
export function getStoreDealLabel(type: string): string {
  const trimmed = type.trim();
  if (!trimmed || /^ends in$/i.test(trimmed)) return FALLBACK_DEAL_LABEL;
  return type;
}
